// biome-ignore-all lint/suspicious/noConsole: Console logging is needed for development debugging
import { autosaveStatusEmitter } from '@/hooks/use-autosave-status';
import { usePageEditorStore } from '@/stores/page-editor-store';
import type { PageEditorAction } from '@/types/page-editor-actions';
import { autosaveClient } from './client';
import {
  AUTOSAVE_DEBOUNCE_MS,
  AUTOSAVE_MAX_RETRY_ATTEMPTS,
  AUTOSAVE_RETRY_BASE_MS,
  AUTOSAVE_RETRY_MAX_MS,
  type AutosaveBatchPayload,
  autosaveContext,
  autosaveRuntimeState,
  clearQueuedAutosave,
  getQueuedAutosaveCount,
  hasQueuedAutosaveActions,
  queuePendingAutosaveAction,
  takePendingAutosaveActions,
} from './state';

interface SendWithRetryParams {
  batch: AutosaveBatchPayload;
  attempt?: number;
}

export function pauseAutosave(): void {
  autosaveRuntimeState.aiOperationPauseCount++;
  if (autosaveRuntimeState.autosaveFlushTimer) {
    clearTimeout(autosaveRuntimeState.autosaveFlushTimer);
    autosaveRuntimeState.autosaveFlushTimer = null;
  }
}

export function resumeAutosave(): void {
  autosaveRuntimeState.aiOperationPauseCount = Math.max(
    0,
    autosaveRuntimeState.aiOperationPauseCount - 1,
  );

  if (
    autosaveRuntimeState.aiOperationPauseCount === 0 &&
    hasQueuedAutosaveActions() &&
    !autosaveRuntimeState.autosaveFlushTimer
  ) {
    autosaveRuntimeState.autosaveFlushTimer = setTimeout(flushAutosaveQueue, AUTOSAVE_DEBOUNCE_MS);
  }
}

function queueAutosaveAction(action: PageEditorAction) {
  if (!autosaveContext.pageId) {
    return;
  }

  queuePendingAutosaveAction(action);
  autosaveStatusEmitter.setSaving();

  const pageEditorStore = usePageEditorStore.getState();
  const currentPage = pageEditorStore.page;
  if (currentPage && !currentPage.hasUnpublishedChanges) {
    pageEditorStore.setPage(
      { ...currentPage, hasUnpublishedChanges: true },
      { resetHistory: false },
    );
  }

  if (
    !autosaveRuntimeState.autosaveFlushTimer &&
    autosaveRuntimeState.aiOperationPauseCount === 0
  ) {
    autosaveRuntimeState.autosaveFlushTimer = setTimeout(flushAutosaveQueue, AUTOSAVE_DEBOUNCE_MS);
  }
}

async function executeFlush(): Promise<void> {
  if (!hasQueuedAutosaveActions()) {
    return;
  }

  const pageId = autosaveContext.pageId;
  if (!pageId) {
    clearQueuedAutosave();
    return;
  }

  const batch: AutosaveBatchPayload = {
    pageId,
    actions: takePendingAutosaveActions(),
  };

  await sendWithRetry({ batch });
  autosaveStatusEmitter.setSaved();
}

/**
 * Sends a batch with automatic exponential backoff retry logic.
 * Throws an error if all retries fail.
 */
async function sendWithRetry({ batch, attempt = 0 }: SendWithRetryParams): Promise<void> {
  try {
    await autosaveClient.send(batch);
    return;
  } catch (_error) {
    const nextAttempt = attempt + 1;

    // Max retries reached - give up
    if (nextAttempt >= AUTOSAVE_MAX_RETRY_ATTEMPTS) {
      autosaveStatusEmitter.setIdle();
      throw new Error('Failed to save after maximum retry attempts');
    }

    // Calculate delay with exponential backoff
    const delay = Math.min(AUTOSAVE_RETRY_MAX_MS, AUTOSAVE_RETRY_BASE_MS * 2 ** nextAttempt);
    console.info(`[Autosave] Retry attempt ${nextAttempt} after ${delay}ms`);

    // Wait and retry
    await new Promise((resolve) => setTimeout(resolve, delay));
    return sendWithRetry({
      batch: { ...batch, attempt: nextAttempt },
      attempt: nextAttempt,
    });
  }
}

/**
 * Returns the count of pending autosave actions.
 * Can be used to check if there are unsaved changes without triggering a flush.
 */
export function getPendingAutosaveCount(): number {
  return getQueuedAutosaveCount();
}

/**
 * Returns true if there are pending autosave actions or a flush is currently in progress.
 * The flush includes automatic retries, so this will stay true until retries complete.
 */
export function hasPendingAutosave(): boolean {
  return hasQueuedAutosaveActions() || autosaveRuntimeState.currentFlushPromise !== null;
}

/**
 * Flushes any pending autosave actions immediately.
 * Returns a promise that resolves when the flush is complete (including all retries).
 * Safe to call multiple times - will return existing flush promise if one is in progress.
 */
export async function flushPendingAutosave(): Promise<void> {
  if (autosaveRuntimeState.autosaveFlushTimer) {
    clearTimeout(autosaveRuntimeState.autosaveFlushTimer);
    autosaveRuntimeState.autosaveFlushTimer = null;
  }

  // If a flush is already in progress, wait for it
  if (autosaveRuntimeState.currentFlushPromise) {
    await autosaveRuntimeState.currentFlushPromise;

    if (hasQueuedAutosaveActions()) {
      return flushPendingAutosave();
    }
    return;
  }

  if (hasQueuedAutosaveActions()) {
    autosaveRuntimeState.currentFlushPromise = executeFlush();
    try {
      await autosaveRuntimeState.currentFlushPromise;
    } finally {
      autosaveRuntimeState.currentFlushPromise = null;
    }
  }
}

function flushAutosaveQueue() {
  autosaveRuntimeState.autosaveFlushTimer = null;
  void flushPendingAutosave();
}

function handleAction(action: PageEditorAction) {
  queueAutosaveAction(action);

  if (import.meta.env.DEV) {
    console.groupCollapsed(`[PageAction] ${action.type}`);
    console.log('Payload:', action.payload);
    console.groupEnd();
  }
}

/**
 * Handles page editor actions (autosave, logging).
 * Call this directly from store methods instead of using a middleware
 */
export function handlePageEditorAction(action: PageEditorAction) {
  handleAction(action);
}
